import { useState } from 'react';

import { ChevronDown, ExternalLink, KeyRound, LogOut, Menu, User } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { useLogout } from '@/hooks/useAuth';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/store/auth.store';

const LANDING_INTERNET = import.meta.env['VITE_INTERNET_URL'] ?? 'http://localhost:5173';
const LANDING_INTRANET = import.meta.env['VITE_INTRANET_URL'] ?? 'http://localhost:5173';

function getInitials(name: string | undefined): string {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

export interface TopbarProps {
  onOpenSidebar: () => void;
}

export function Topbar({ onOpenSidebar }: TopbarProps) {
  const user = useAuthStore((state) => state.user);
  const logout = useLogout();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-20 flex h-nav items-center justify-between border-b border-primary-100 bg-white px-4 lg:px-6">
      <Button id="btn_OpenNavigation" variant="ghost" size="sm" onClick={onOpenSidebar} className="lg:hidden" aria-label="Open navigation">
        <Menu className="h-5 w-5" aria-hidden="true" />
      </Button>

      <div className="flex flex-1 items-center justify-end gap-2">
        <a
          id="lbl_ViewInternet"
          href={LANDING_INTERNET}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden items-center gap-1.5 rounded-btn border border-primary-200 bg-primary-100/60 px-3 py-2
                     text-sm font-medium text-primary transition-colors duration-150 ease-premium
                     hover:bg-primary-100 sm:inline-flex"
        >
          View Internet
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </a>

        <a
          id="lbl_ViewIntranet"
          href={LANDING_INTRANET}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden items-center gap-1.5 rounded-btn border border-primary-200 bg-primary-100/60 px-3 py-2
                     text-sm font-medium text-primary transition-colors duration-150 ease-premium
                     hover:bg-primary-100 sm:inline-flex"
        >
          View Intranet
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </a>

        <div className="relative">
          <button
            id="btn_AccountMenu"
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            className="flex items-center gap-2.5 rounded-btn py-1.5 pl-1.5 pr-2.5 transition-colors duration-150
                       ease-premium hover:bg-primary-100"
          >
            <span
              aria-hidden="true"
              className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-surface-page"
            >
              {user?.avatarUrl ? (
                <img src={user.avatarUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="text-xs font-semibold text-gray-500">{getInitials(user?.name)}</span>
              )}
            </span>
            <span className="hidden text-left leading-tight sm:block">
              <span className="block text-sm font-semibold text-gray-900">{user?.name}</span>
              <span className="block text-caption font-normal text-gray-700">{user?.role.name}</span>
            </span>
            <ChevronDown
              className={cn('h-4 w-4 text-primary-500 transition-transform duration-200 ease-premium', menuOpen && 'rotate-180')}
              aria-hidden="true"
            />
          </button>

          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} aria-hidden="true" />

              <div
                role="menu"
                className="absolute right-0 z-20 mt-2 w-60 animate-slide-up overflow-hidden rounded-card
                           border border-gray-300/60 bg-white shadow-dropdown"
              >
                <div className="border-b border-primary-200 bg-primary-100 px-4 py-3">
                  <p className="truncate text-sm font-semibold text-primary">{user?.name}</p>
                  <p className="truncate text-caption font-normal text-gray-700">{user?.email}</p>
                </div>

                <div className="p-1.5">
                  <Link
                    id="lbl_MenuProfile"
                    to="/profile"
                    role="menuitem"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 rounded-btn px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-primary-100 hover:text-primary"
                  >
                    <User className="h-4 w-4" aria-hidden="true" />
                    Profile
                  </Link>

                  <Link
                    id="lbl_MenuChangePassword"
                    to="/profile/password"
                    role="menuitem"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 rounded-btn px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-primary-100 hover:text-primary"
                  >
                    <KeyRound className="h-4 w-4" aria-hidden="true" />
                    Change password
                  </Link>

                  <button
                    id="btn_SignOut"
                    type="button"
                    role="menuitem"
                    onClick={() => logout.mutate()}
                    disabled={logout.isPending}
                    className="flex w-full items-center gap-2.5 rounded-btn px-3 py-2 text-sm text-burgundy
                               hover:bg-burgundy-100 disabled:opacity-55"
                  >
                    <LogOut className="h-4 w-4" aria-hidden="true" />
                    {logout.isPending ? 'Signing out…' : 'Sign out'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
